/**
 * Petit wrapper d'appels à l'API billing.
 *
 * Les routes sont exposées par `webtarotNeo/backend/src/routes/billing.ts`
 * qui proxify vers le module billing de webtarotAPI.
 */

import { BACKEND_URL } from '../config';

export type BillingTier = 'free' | 'competition' | 'classic_plus';
export type BillingPeriod = 'yearly' | 'monthly';
export type SubscriptionStatus =
  | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid'
  | 'incomplete' | 'incomplete_expired' | 'paused' | 'expired';

export interface CurrentSubscription {
  pseudo: string;
  tier: BillingTier;
  status: SubscriptionStatus;
  provider: 'stripe' | 'cmcic_legacy' | 'apple_iap' | 'google_iap';
  providerRef: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEnd: string | null;
  cancelAt: string | null;
  metadata: Record<string, unknown>;
}

export interface BillingCatalog {
  tiers: Array<{ tier: BillingTier; billings: BillingPeriod[] }>;
  trialDays: number;
}

export interface CheckoutPayload {
  tier: BillingTier;
  billing: BillingPeriod;
  withTrial?: boolean;
  identityKey?: string;
  identityType?: 'oauth_google' | 'oauth_facebook' | 'oauth_apple' | 'email';
}

function authHeaders(token?: string): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchCatalog(): Promise<BillingCatalog> {
  const res = await fetch(`${BACKEND_URL}/api/billing/catalog`);
  if (!res.ok) throw new Error(`catalog_fetch_failed_${res.status}`);
  return res.json();
}

export async function fetchMySubscription(token: string): Promise<CurrentSubscription | null> {
  const res = await fetch(`${BACKEND_URL}/api/billing/me`, {
    headers: authHeaders(token),
  });
  if (!res.ok) {
    if (res.status === 401) return null;
    throw new Error(`me_fetch_failed_${res.status}`);
  }
  const data = await res.json();
  return data.subscription || null;
}

export async function createCheckout(
  token: string,
  payload: CheckoutPayload,
): Promise<{ url: string; sessionId: string }> {
  const res = await fetch(`${BACKEND_URL}/api/billing/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || `checkout_failed_${res.status}`);
  return { url: data.url, sessionId: data.sessionId };
}

export async function openCustomerPortal(token: string): Promise<{ url: string }> {
  const res = await fetch(`${BACKEND_URL}/api/billing/portal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || `portal_failed_${res.status}`);
  return { url: data.url };
}
